import {
  CircleUserRound,
  HelpCircle,
  Key,
  LogOut,
  Menu,
  Settings,
  Smartphone,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { useCoachOnboardingContext } from '../../contexts/CoachOnboardingContext'
import { useRole } from '../../contexts/RoleContext'
import { useAthleteCoach } from '../../hooks/useAthleteCoach'
import { FeatureFlagKey, useFeatureFlags } from '../../hooks/useFeatureFlags'
import { useProfile } from '../../hooks/useProfile'
import type { UserRoleType } from '../../lib/supabase'
import { getAvatarUrl } from '../../utils/avatarUtils'
import { InviteAthleteModal } from '../coach/InviteAthleteModal'
import { ActivityUploadButton } from '../common/ActivityUploadButton'
import { ChangePasswordModal } from '../common/ChangePasswordModal'
import { ProfileSettingsModal } from '../common/ProfileSettingsModal'
import { ThemeSwitcher } from '../common/ThemeSwitcher'
import { UploadStatusNotification, useUploadStatus } from '../common/UploadStatusNotification'

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.background};
`

const HeaderContent = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const MainHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};
`

const HeaderCenter = styled.div`
  padding-left: ${({ theme }) => theme.spacing.xxl};
  display: flex;
  flex-grow: 1;

  @media (max-width: 768px) {
    display: none;
  }
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.0rem;
  font-weight: bold;
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.md};
`

const LogoText = styled.h1`
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  font-size: ${({ theme }) => theme.typography.xl};
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  text-align: center;
`

const Logo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: contain;
  margin: 0 auto;
`

const UserMenuContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`

const UserMenuButton = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: none;
  padding: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
  }
`

const UserDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: ${({ theme }) => theme.transitions.default};
  z-index: 1000;
  min-width: 200px;
`

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.md};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
  
  &:first-child {
    border-radius: ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm};
  }
`

const RoleSection = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  .role-label {
    font-size: ${({ theme }) => theme.typography.xs};
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  .current-role {
    font-size: ${({ theme }) => theme.typography.sm};
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
    text-transform: capitalize;
  }

  .coach-info {
    margin-top: ${({ theme }) => theme.spacing.sm};
    padding-top: ${({ theme }) => theme.spacing.sm};
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }

  .coach-label {
    font-size: ${({ theme }) => theme.typography.xs};
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }

  .coach-name {
    font-size: ${({ theme }) => theme.typography.sm};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
  }
`

const RoleSwitcher = styled.div`
  padding: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const RoleSwitchButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${(props) => (props.$isActive ? `${props.theme.colors.primary}20` : 'none')};
  border: none;
  color: ${(props) => (props.$isActive ? props.theme.colors.primary : props.theme.colors.text)};
  font-size: ${({ theme }) => theme.typography.sm};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  text-transform: capitalize;
  
  &:hover {
    background-color: ${(props) => (props.$isActive ? `${props.theme.colors.primary}30` : props.theme.colors.surfaceHover)};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const TabNavigation = styled.div`
  display: flex;
  gap: 0;
  height: 50px;
  align-items: stretch;
`

const Tab = styled.button<{ $isActive: boolean }>`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.textSecondary)};
  cursor: pointer;
  border-bottom: 3px solid ${(props) => (props.$isActive ? props.theme.colors.primary : 'transparent')};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  
  &:hover {
    color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.text)};
    border-bottom: 3px solid ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.primaryLight)};
  }
`

const InviteButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textInverted};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}40;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const BurgerButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: 0;
  margin-right: ${({ theme }) => theme.spacing.sm};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  @media (max-width: 768px) {
    display: flex;
  }
`

const MobileMenuOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.background};
    z-index: 1000;
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform 0.3s ease;
    overflow-y: auto;
  }
`

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const MobileMenuContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`

const MobileMenuItem = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme, $isActive }) => ($isActive ? theme.colors.surface : 'none')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.text)};
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface};
  }
`

const MobileMenuSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const MobileMenuSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding: 0 ${({ theme }) => theme.spacing.md};
`

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`

type TabType =
  | 'calendar'
  | 'recovery'
  | 'physiology'
  | 'testing'
  | 'races'
  | 'diagnostics'
  | 'athletes'
  | 'automations'
  | 'analysis'

interface TabConfig {
  id: TabType
  label: string
  description: string
}

export const AppHeader: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { signOut, session, user } = useAuth()
  const { currentRole, availableRoles, switchRole, loading: roleLoading } = useRole()
  const { data: enabledFeatures } = useFeatureFlags()
  const { profile } = useProfile()
  const { resetOnboarding } = useCoachOnboardingContext()

  // Only fetch coach if user is not a coach themselves
  const isUserCoach = availableRoles.includes('coach')
  const { coach } = useAthleteCoach(!isUserCoach ? user?.id : undefined)

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showProfileSettings, setShowProfileSettings] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const { status: uploadStatus, showSuccess, showError, clearStatus } = useUploadStatus()

  // Extract athlete ID from route if we're on athlete detail page
  const athleteDetailMatch = matchPath({ path: '/athletes/:id' }, location.pathname)

  const athleteId = athleteDetailMatch?.params.id
  const isAthleteView = currentRole === 'athlete'
  const isCoachesView = currentRole === 'coach'

  // For coaches viewing athlete details, show upload button for that athlete
  const showUploadButton = isAthleteView || (isCoachesView && athleteId)

  // Tab logic for athlete details
  const currentTab = searchParams.get('tab') as TabType
  const tabs: TabConfig[] = []

  // fill tabs depending on users role and feature flags
  if (isAthleteView) {
    if (enabledFeatures?.some((f) => f.flag_key === FeatureFlagKey.CalendarView)) {
      tabs.push({ id: 'calendar' as TabType, label: 'Training', description: 'Weekly training calendar' })
    }
    if (enabledFeatures?.some((f) => f.flag_key === FeatureFlagKey.Testing)) {
      tabs.push({ id: 'testing' as TabType, label: 'Testing', description: 'Performance tests and assessments' })
    }
    if (enabledFeatures?.some((f) => f.flag_key === FeatureFlagKey.Races)) {
      tabs.push({ id: 'races' as TabType, label: 'Races', description: 'Races and results' })
    }
    // tabs.push({ id: 'diagnostics' as TabType, label: 'Diagnostics', description: 'VO2 max and metabolic test data' })
  } else if (isCoachesView) {
    tabs.push({ id: 'athletes' as TabType, label: 'Athletes', description: 'My Athletes' })
    if (enabledFeatures?.some((f) => f.flag_key === FeatureFlagKey.CoachAnalysis)) {
      tabs.push({ id: 'analysis' as TabType, label: 'Analysis', description: 'Deep Analysis' })
    }
    if (enabledFeatures?.some((f) => f.flag_key === FeatureFlagKey.CoachAutomations)) {
      tabs.push({ id: 'automations' as TabType, label: 'Automations', description: 'Scheduled Analysis' })
    }
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setUserMenuOpen(false)
    if (userMenuOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [userMenuOpen])

  const handleLogout = async () => {
    console.log('Logout clicked')
    setUserMenuOpen(false)

    try {
      console.log('Calling signOut...')
      const result = await signOut()
      console.log('SignOut result:', result)

      console.log('Navigating to login...')
      navigate('/login')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  const handleSettings = () => {
    setUserMenuOpen(false)
    setShowProfileSettings(true)
  }

  const handleChangePassword = () => {
    setUserMenuOpen(false)
    setShowChangePassword(true)
  }

  const handleRestartTour = () => {
    setUserMenuOpen(false)
    // Navigate to athletes page first to ensure tour elements are present
    navigate('/athletes')
    // Wait for navigation to complete, then reset and start the tour
    setTimeout(() => {
      resetOnboarding()
    }, 300)
  }

  const handleConnectedDevices = () => {
    setUserMenuOpen(false)
    setMobileMenuOpen(false)
    // Navigate to dashboard with connect tab
    navigate('/dashboard?tab=connect')
  }

  const handleInviteAthlete = () => {
    setShowInviteModal(true)
  }

  const handleTabChange = (tabId: TabType) => {
    if (tabId === 'athletes') {
      navigate('/athletes')
    } else if (tabId === 'automations') {
      navigate('/automations')
    } else if (tabId === 'analysis') {
      navigate('/analysis')
    } else {
      setSearchParams({ tab: tabId })
    }
    setMobileMenuOpen(false)
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const handleRoleSwitch = async (role: UserRoleType) => {
    if (role === currentRole) {
      return
    }

    try {
      const result = await switchRole(role)
      if (result.error) {
        console.error('Error switching role:', result.error)
        // TODO: Show error message to user
      } else {
        console.log(`Successfully switched to ${role} role`)
        // Redirect to appropriate dashboard based on new role
        if (role === 'coach') {
          navigate('/athletes')
          // set current tab to 'athletes'
        } else {
          navigate('/dashboard')
        }
        // close role switcher
        setUserMenuOpen(false)
      }
    } catch (error) {
      console.error('Exception switching role:', error)
    }
  }

  const handleUploadSuccess = (_workoutId: string, workoutTitle: string, workoutDate: string) => {
    showSuccess(`${workoutDate} ${workoutTitle} uploaded successfully`)
    // TODO: add link to workout in notification
    // Dispatch custom event to trigger calendar reload
    window.dispatchEvent(new CustomEvent('workout-uploaded'))
  }

  const handleUploadError = (error: string) => {
    showError(`Error uploading activity: ${error}`)
  }

  const toggleUserMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    setUserMenuOpen(!userMenuOpen)
  }

  const renderMobileMenu = () => {
    return (
      <MobileMenuOverlay $isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <LogoText>supacoach</LogoText>
          <BurgerButton onClick={toggleMobileMenu}>
            <X size={24} />
          </BurgerButton>
        </MobileMenuHeader>

        <MobileMenuContent>
          {/* Navigation Tabs */}
          {tabs.map((tab) => {
            const isActive = currentTab === tab.id || tab.id === location.pathname.substring(1)
            return (
              <MobileMenuItem key={tab.id} $isActive={isActive} onClick={() => handleTabChange(tab.id)}>
                {tab.label}
              </MobileMenuItem>
            )
          })}

          {/* Coach Actions */}
          {isCoachesView && (
            <MobileMenuSection>
              <MobileMenuSectionTitle>Actions</MobileMenuSectionTitle>
              <MobileMenuItem
                onClick={() => {
                  handleInviteAthlete()
                  setMobileMenuOpen(false)
                }}
              >
                <UserPlus size={18} />
                Invite Athlete
              </MobileMenuItem>
              <div style={{ padding: '0 1rem' }}>
                <ThemeSwitcher />
              </div>
            </MobileMenuSection>
          )}

          {/* User Menu */}
          <MobileMenuSection>
            <MobileMenuSectionTitle>Account</MobileMenuSectionTitle>

            {/* Coach Info for Athletes */}
            {!isUserCoach && coach && (
              <div style={{ padding: '0.75rem 1rem', marginBottom: '0.5rem' }}>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#888',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    marginBottom: '0.25rem',
                  }}
                >
                  Your Coach
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {coach.coach_first_name} {coach.coach_last_name}
                </div>
              </div>
            )}

            {/* Role Switcher */}
            {availableRoles.length > 1 && (
              <>
                <MobileMenuSectionTitle>Switch Role</MobileMenuSectionTitle>
                {availableRoles.map((role) => (
                  <MobileMenuItem
                    key={role}
                    $isActive={role === currentRole}
                    onClick={() => {
                      handleRoleSwitch(role)
                      setMobileMenuOpen(false)
                    }}
                  >
                    <CircleUserRound size={18} />
                    {role}
                  </MobileMenuItem>
                ))}
              </>
            )}

            {isAthleteView && (
              <MobileMenuItem onClick={handleConnectedDevices}>
                <Smartphone size={18} />
                Connected Devices
              </MobileMenuItem>
            )}

            {isCoachesView && (
              <>
                <MobileMenuItem
                  onClick={() => {
                    handleChangePassword()
                    setMobileMenuOpen(false)
                  }}
                >
                  <Key size={18} />
                  Change Password
                </MobileMenuItem>
                <MobileMenuItem
                  onClick={() => {
                    handleRestartTour()
                    setMobileMenuOpen(false)
                  }}
                >
                  <HelpCircle size={18} />
                  Restart Tour
                </MobileMenuItem>
              </>
            )}

            <MobileMenuItem onClick={handleLogout}>
              <LogOut size={18} />
              Sign Out
            </MobileMenuItem>
          </MobileMenuSection>
        </MobileMenuContent>
      </MobileMenuOverlay>
    )
  }

  const renderUserMenu = () => {
    if (!session) {
      return null
    }

    const initials = (profile?.first_name?.[0] || '') + (profile?.last_name?.[0] || '')
    const avatarUrl = profile?.avatar_url ? getAvatarUrl(profile.avatar_url) : null

    return (
      <UserMenuContainer data-tour="user-menu">
        <UserMenuButton onClick={toggleUserMenu}>
          <Avatar>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={initials}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </Avatar>
        </UserMenuButton>

        <UserDropdown $isOpen={userMenuOpen} onClick={(e) => e.stopPropagation()}>
          {/* Current Role Display */}
          <RoleSection>
            <div className="role-label">Current Role</div>
            <div className="current-role">
              <UserCheck size={14} style={{ display: 'inline', marginRight: '4px' }} />
              {currentRole || 'athlete'}
            </div>

            {/* Coach Info for Athletes */}
            {coach && (
              <div className="coach-info">
                <div className="coach-label">Your Coach</div>
                <div className="coach-name">
                  {coach.coach_first_name} {coach.coach_last_name}
                </div>
              </div>
            )}
          </RoleSection>

          {/* Role Switcher */}
          {availableRoles.length > 1 && (
            <RoleSwitcher>
              {availableRoles.map((role) => (
                <RoleSwitchButton
                  key={role}
                  $isActive={role === currentRole}
                  disabled={roleLoading || role === currentRole}
                  onClick={() => handleRoleSwitch(role)}
                >
                  <CircleUserRound size={16} />
                  {role}
                </RoleSwitchButton>
              ))}
            </RoleSwitcher>
          )}

          <DropdownItem onClick={handleSettings}>
            <Settings size={16} />
            Settings
          </DropdownItem>
          {isAthleteView && (
            <DropdownItem onClick={handleConnectedDevices}>
              <Smartphone size={16} />
              Connected Devices
            </DropdownItem>
          )}
          {isCoachesView && (
            <>
              <DropdownItem onClick={handleChangePassword}>
                <Key size={16} />
                Change Password
              </DropdownItem>
              <DropdownItem onClick={handleRestartTour}>
                <HelpCircle size={16} />
                Restart Tour
              </DropdownItem>
            </>
          )}
          <DropdownItem onClick={handleLogout}>
            <LogOut size={16} />
            Sign Out
          </DropdownItem>
        </UserDropdown>
      </UserMenuContainer>
    )
  }

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <MainHeader>
            <HeaderLeft>
              <LogoContainer>
                <Logo src="/logo_512.png" alt="supacoach Logo" />
                <LogoText>supacoach</LogoText>
              </LogoContainer>
            </HeaderLeft>

            <HeaderCenter>
              <TabNavigation>
                {tabs.map((tab, _i) => {
                  const isActive = currentTab === tab.id || tab.id === location.pathname.substring(1)
                  return (
                    <Tab
                      key={tab.id}
                      $isActive={isActive}
                      onClick={() => handleTabChange(tab.id)}
                      title={tab.description}
                      data-tour={`tab-${tab.id}`}
                    >
                      {tab.label}
                    </Tab>
                  )
                })}
              </TabNavigation>
            </HeaderCenter>

            <HeaderRight>
              <BurgerButton onClick={toggleMobileMenu}>
                <Menu size={24} />
              </BurgerButton>
              {isCoachesView && (
                <InviteButton data-tour="invite-button" onClick={handleInviteAthlete}>
                  <UserPlus size={16} />
                  Invite Athlete
                </InviteButton>
              )}
              {showUploadButton && athleteId && (
                <ActivityUploadButton
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                  athleteId={athleteId}
                />
              )}
              {isCoachesView && (
                <DesktopOnly>
                  <ThemeSwitcher />
                </DesktopOnly>
              )}
              {renderUserMenu()}
            </HeaderRight>
          </MainHeader>
        </HeaderContent>
      </HeaderContainer>
      {renderMobileMenu()}
      {showUploadButton && <UploadStatusNotification status={uploadStatus} onDismiss={clearStatus} />}
      <ProfileSettingsModal isOpen={showProfileSettings} onClose={() => setShowProfileSettings(false)} />
      {isCoachesView && (
        <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />
      )}
      <InviteAthleteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        coachId={session?.user?.id || ''}
        coachName={
          `${session?.user?.user_metadata?.first_name || ''} ${session?.user?.user_metadata?.last_name || ''}`.trim() ||
          session?.user?.email ||
          'Coach'
        }
      />
    </>
  )
}
